import { useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cx } from '../utils/cx.js';
import styles from './Checkbox.module.css';
import inputStyles from './Input.module.css';

export type CheckboxProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  label?: ReactNode;
  hint?: string;
  error?: string;
  showErrorText?: boolean;
};

export function Checkbox({
  label,
  hint,
  error,
  id,
  className,
  disabled,
  required,
  showErrorText = true,
  'aria-describedby': ariaDescribedBy,
  ...rest
}: CheckboxProps) {
  const uid = useId();
  const cid = id ?? rest.name ?? uid;
  const errorId = `${cid}-error`;
  const describedBy = [ariaDescribedBy, error ? errorId : null].filter(Boolean).join(' ') || undefined;

  return (
    <div className={cx(inputStyles.wrap, className)}>
      <label className={cx(styles.wrap, disabled && styles.disabled)} htmlFor={cid}>
        <input
          id={cid}
          type="checkbox"
          className={styles.input}
          disabled={disabled}
          required={required}
          aria-invalid={!!error}
          aria-required={required || undefined}
          aria-describedby={describedBy}
          {...rest}
        />
        <span className={styles.box} aria-hidden />
        {label ? <span className={styles.label}>{label}</span> : null}
      </label>
      {showErrorText && error ? (
        <span id={errorId} className={inputStyles.errText} role="alert">
          {error}
        </span>
      ) : hint ? (
        <span className={inputStyles.hint}>{hint}</span>
      ) : null}
    </div>
  );
}
